import type { CreateVehicleTO } from '@control-center/control-center-client-public-vehicles-ts';
import { zodResolver } from '@hookform/resolvers/zod';
import FormDialogFooter from 'components/forms/FormDialogFooter';
import FormTextInput from 'components/forms/FormTextInput';
import SingleSelect, { type SelectOption } from 'components/forms/SingleSelect';
import Tooltip from 'components/overlays/Tooltip';
import { type ReactElement, type ReactNode, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { v4 as uuid } from 'uuid';
import { z } from 'zod';

import { getVehicleTypeIdentifiers, SUPPORTED_VEHICLE_KINDS, type VehicleKind } from '../systemVehicleUtils';
import styles from './styles/VehicleDialog.module.scss';
import VehicleSubmitErrorAlert from './VehicleSubmitErrorAlert';
import { normalizeVehicleFields, vehicleLicensePlateSchema, vehicleNameSchema } from './vehicleFormSchema';

const PHYSICAL: VehicleKind = 'PHYSICAL';
const SIMULATOR: VehicleKind = 'SIMULATOR';
const HW_RIG: VehicleKind = 'HW_RIG';
const SIMULATED_CONNECTIVITY: VehicleKind = 'SIMULATED_CONNECTIVITY';

const vehicleTypeOptions: SelectOption[] = [
    { id: PHYSICAL, label: 'Physical' },
    { id: HW_RIG, label: 'HW rig' },
    { id: SIMULATOR, label: 'Simulated (agent)' },
    { id: SIMULATED_CONNECTIVITY, label: 'Simulated (connectivity)' },
];

const NON_PHYSICAL_VEHICLE_KINDS = new Set<VehicleKind>([SIMULATOR, HW_RIG, SIMULATED_CONNECTIVITY]);
const VIN_REGEX = /^[A-HJ-NPR-Z0-9]{17}$/;
const VEHICLE_TYPE_LABEL_ID = 'vehicle-type-label';
const VEHICLE_TYPE_ERROR_ID = 'vehicle-type-error';

const createVehicleFormSchema = z
    .object({
        vehicleKind: z.enum(SUPPORTED_VEHICLE_KINDS, {
            error: issue =>
                issue.input === undefined || issue.input === '' ? 'Type is required' : 'Unsupported vehicle type',
        }),
        name: vehicleNameSchema,
        licensePlate: vehicleLicensePlateSchema,
        vin: z.string(),
        equipmentId: z.string(),
    })
    .superRefine((data, context) => {
        const isPhysical = data.vehicleKind === PHYSICAL;
        if (data.name.trim().length === 0) {
            context.addIssue({
                code: 'custom',
                path: ['name'],
                message: 'Vehicle name is required',
            });
        }
        if (!isPhysical) {
            return;
        }
        if (!VIN_REGEX.test(data.vin)) {
            context.addIssue({
                code: 'custom',
                path: ['vin'],
                message: 'Invalid VIN',
            });
        }
        if (!z.guid().safeParse(data.equipmentId.trim()).success) {
            context.addIssue({
                code: 'custom',
                path: ['equipmentId'],
                message: 'Invalid Equipment ID',
            });
        }
    });

type CreateVehicleFormData = z.infer<typeof createVehicleFormSchema>;

interface CreateVehicleFormProps {
    show: boolean;
    isSubmitting: boolean;
    onCreate: (vehicle: CreateVehicleTO) => void | Promise<void>;
    requestCancel: () => void;
    onDirtyChange: (isDirty: boolean) => void;
    renderDialog?: (form: ReactElement) => ReactNode;
}

const isConflictError = (error: unknown): boolean =>
    typeof error === 'object' && error !== null && 'status' in error && error.status === 409;

const CreateVehicleForm = ({
    show,
    isSubmitting,
    onCreate,
    requestCancel,
    onDirtyChange,
    renderDialog,
}: CreateVehicleFormProps) => {
    const {
        register,
        control,
        handleSubmit,
        watch,
        getValues,
        clearErrors,
        setError,
        reset,
        formState: { errors, isValid, isDirty },
    } = useForm<CreateVehicleFormData>({
        resolver: zodResolver(createVehicleFormSchema),
        mode: 'onChange',
        defaultValues: {
            vehicleKind: undefined,
            name: '',
            licensePlate: '',
            vin: '',
            equipmentId: '',
        },
    });

    const [submitFailed, setSubmitFailed] = useState(false);
    const [vinAlreadyRegistered, setVinAlreadyRegistered] = useState(false);

    const selectedVehicleKind = watch('vehicleKind');
    const isPhysicalVehicle = selectedVehicleKind === PHYSICAL;
    const isNonPhysicalVehicle =
        selectedVehicleKind !== undefined && NON_PHYSICAL_VEHICLE_KINDS.has(selectedVehicleKind);
    const hasVehicleFields = isPhysicalVehicle || isNonPhysicalVehicle;

    useEffect(() => {
        onDirtyChange(isDirty);
    }, [isDirty, onDirtyChange]);

    useEffect(() => {
        if (!show) {
            reset();
            setSubmitFailed(false);
            setVinAlreadyRegistered(false);
        }
    }, [show, reset]);

    const submitVehicle = async (formData: CreateVehicleFormData) => {
        const editableFields = normalizeVehicleFields(formData);
        const kind = formData.vehicleKind;

        clearErrors('vin');
        setSubmitFailed(false);
        setVinAlreadyRegistered(false);

        const payload: CreateVehicleTO = NON_PHYSICAL_VEHICLE_KINDS.has(kind)
            ? {
                  vin: null,
                  externalId: uuid(),
                  ...editableFields,
                  ...getVehicleTypeIdentifiers(kind),
              }
            : {
                  vin: formData.vin.trim().toUpperCase(),
                  externalId: formData.equipmentId.trim(),
                  ...editableFields,
                  ...getVehicleTypeIdentifiers(PHYSICAL),
              };

        try {
            await onCreate(payload);
        } catch (error) {
            const hasVinConflict = isConflictError(error);
            if (hasVinConflict) {
                const currentValues = getValues();
                const submittedPhysicalVin = kind === PHYSICAL ? formData.vin.trim().toUpperCase() : null;
                const isStaleConflict =
                    currentValues.vehicleKind !== kind ||
                    (submittedPhysicalVin !== null && currentValues.vin.trim().toUpperCase() !== submittedPhysicalVin);

                if (isStaleConflict) {
                    return;
                }
            }

            setSubmitFailed(true);
            setVinAlreadyRegistered(hasVinConflict);
            if (hasVinConflict) {
                setError('vin', { type: 'server', message: 'VIN already registered' });
            }
        }
    };

    const form = (
        <form className={`${styles.form} font-body`} onSubmit={handleSubmit(submitVehicle)}>
            <div className='display-flex flex-column gap-20 margin-bottom-20'>
                <div className='display-flex flex-column gap-15 margin-bottom-20'>
                    <h5>Add vehicle information</h5>
                    <div className={`form-group mb-0 ${errors.vehicleKind ? 'has-error has-feedback' : ''}`}>
                        <label id={VEHICLE_TYPE_LABEL_ID} htmlFor='vehicle-type'>
                            <Tooltip trigger={<span>Type</span>} placement='top-start' variant='plain'>
                                <p>Mandatory field</p>
                            </Tooltip>
                            <span className='text-color-danger' aria-hidden='true'>
                                *
                            </span>
                        </label>
                        <Controller
                            control={control}
                            name='vehicleKind'
                            render={({ field }) => (
                                <SingleSelect
                                    id='vehicle-type'
                                    name='vehicle-type'
                                    placeholder='Select type'
                                    options={vehicleTypeOptions}
                                    value={field.value}
                                    onChange={value => {
                                        field.onChange(value || undefined);
                                        clearErrors('vin');
                                        setVinAlreadyRegistered(false);
                                        setSubmitFailed(false);
                                    }}
                                    hasError={!!errors.vehicleKind}
                                />
                            )}
                        />
                        {errors.vehicleKind && (
                            <span id={VEHICLE_TYPE_ERROR_ID} className='help-block' role='alert'>
                                {errors.vehicleKind.message}
                            </span>
                        )}
                    </div>

                    {hasVehicleFields && (
                        <div className='display-flex flex-column gap-15'>
                            <FormTextInput
                                id='vehicle-name'
                                label='Vehicle name'
                                placeholder='Start typing'
                                autoComplete='off'
                                mandatory
                                registration={register('name')}
                                error={errors.name?.message}
                                className='font-body'
                            />
                            <FormTextInput
                                id='vehicle-license-plate'
                                label='License plate'
                                placeholder='Start typing'
                                autoComplete='off'
                                registration={register('licensePlate')}
                                error={errors.licensePlate?.message}
                                className='font-body'
                            />

                            {isPhysicalVehicle && (
                                <>
                                    <FormTextInput
                                        id='vehicle-equipment-id'
                                        label='Equipment ID'
                                        placeholder='Start typing'
                                        autoComplete='off'
                                        mandatory
                                        registration={register('equipmentId')}
                                        error={errors.equipmentId?.message}
                                        className='font-body'
                                    />

                                    <FormTextInput
                                        id='vehicle-vin'
                                        label='VIN'
                                        placeholder='Start typing'
                                        autoComplete='off'
                                        maxLength={17}
                                        mandatory
                                        registration={register('vin', {
                                            onChange: () => {
                                                clearErrors('vin');
                                                setVinAlreadyRegistered(false);
                                                setSubmitFailed(false);
                                            },
                                        })}
                                        error={errors.vin?.message}
                                        className='font-body'
                                    />
                                </>
                            )}
                        </div>
                    )}
                </div>

                {submitFailed && (
                    <VehicleSubmitErrorAlert title='Failed to create new vehicle'>
                        {vinAlreadyRegistered
                            ? 'Vehicle could not be created due to the VIN already being registered to another vehicle.'
                            : 'Vehicle could not be created due to a technical issue. Please try again or refresh the page. If the error persists contact the control center support team.'}
                    </VehicleSubmitErrorAlert>
                )}
            </div>

            <FormDialogFooter
                onCancel={requestCancel}
                isValid={isValid}
                isLoading={isSubmitting}
                onConfirmLabel='Create new vehicle'
            />
        </form>
    );

    return renderDialog?.(form) ?? form;
};

export default CreateVehicleForm;
